import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientService, documentService, invoiceService, reportService, chequeService, commentService, clientLogService, planService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Client, Invoice, Comment } from '../types';
import { Users, Search, Mail, Phone, Building2, Plus, Edit2, Edit3, X, Save, FileText, MapPin, CreditCard, Calendar, ChevronRight, Upload, Download, Trash2, File, Eye, FileUp, DollarSign, BarChart3, XCircle, Banknote, MessageSquare, Send, User, BookOpen } from 'lucide-react';

interface ClientDocument {
  id: number;
  filename: string;
  original_name: string;
  file_type: string;
  file_size: number;
  document_date: string;
  description: string;
  created_at: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
}

interface Cheque {
  id: number;
  client_id: number;
  fecha_ck: string;
  plan: string;
  check_number: string;
  seguimiento_ck: string;
  cantidad: string;
  factura: string;
  notas: string;
  facturador: string;
  facturado_desde: string;
  facturado_hasta: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by_first_name: string;
  created_by_last_name: string;
  logs?: ChequeLog[];
  facturas?: ChequeFactura[];
  clientNotas?: ChequeNota[];
}

interface ChequeLog {
  id: number;
  cheque_id: number;
  user_id: number;
  facturador: string;
  nota: string;
  created_at: string;
  first_name: string;
  last_name: string;
}

interface ChequeFactura {
  id: number;
  cheque_id: number;
  factura_numero: string;
  notas: string;
  facturador: string;
  created_at: string;
  created_by_first_name: string;
  created_by_last_name: string;
}

interface ChequeNota {
  id: number;
  cheque_id: number;
  nota: string;
  created_at: string;
  created_by_first_name: string;
  created_by_last_name: string;
}

interface ClientFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  initials: string;
  companyName: string;
  phone: string;
  address: string;
  taxId: string;
}

const emptyFormData: ClientFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  initials: '',
  companyName: '',
  phone: '',
  address: '',
  taxId: ''
};

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
  in_process: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Proceso' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completado' },
  ajuste: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Ajuste' },
  denied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Denegado' },
  paid: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Pagado' },
  refacturar: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Re-Facturar' },
};

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<ClientFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeTab, setActiveTab] = useState<'invoices' | 'docs_reports' | 'paid' | 'refacturar' | 'denied' | 'cheques' | 'client_log'>('invoices');
  const [docsReportsSubTab, setDocsReportsSubTab] = useState<'documents' | 'reports'>('documents');
  const [clientDocuments, setClientDocuments] = useState<ClientDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDate, setUploadDate] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<ClientDocument | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const chequeImportRef = useRef<HTMLInputElement>(null);
  
  // New Invoice Modal state
  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [savingNewInvoice, setSavingNewInvoice] = useState(false);
  const [newInvoiceError, setNewInvoiceError] = useState('');
  const [billingPlans, setBillingPlans] = useState<{id: number; plan_code: string; plan_name: string}[]>([]);
  const [newInvoiceData, setNewInvoiceData] = useState({
    invoiceNumber: '',
    planId: '',
    serviceDate: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    amount: '',
    status: 'pending',
    pendingNotes: '',
    managementNotes: '',
    initials: ''
  });
  
  // Cheques state
  const [clientCheques, setClientCheques] = useState<Cheque[]>([]);
  const [loadingCheques, setLoadingCheques] = useState(false);
  const [importingCheques, setImportingCheques] = useState(false);
  const [chequeImportResult, setChequeImportResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const [chequeSearch, setChequeSearch] = useState('');
  const [chequeStatusFilter, setChequeStatusFilter] = useState<string>('all');
  const [selectedCheque, setSelectedCheque] = useState<Cheque | null>(null);
  const [showChequeLogModal, setShowChequeLogModal] = useState(false);
  const [newLogNote, setNewLogNote] = useState('');
  const [addingLog, setAddingLog] = useState(false);
  const [newFacturaNumero, setNewFacturaNumero] = useState('');
  const [newFacturaNotas, setNewFacturaNotas] = useState('');
  const [addingFactura, setAddingFactura] = useState(false);
  const [chequeDetailTab, setChequeDetailTab] = useState<'facturas' | 'notas' | 'log'>('facturas');
  const [newClientNota, setNewClientNota] = useState('');
  const [addingClientNota, setAddingClientNota] = useState(false);
  const [isEditingCheque, setIsEditingCheque] = useState(false);
  const [editChequeData, setEditChequeData] = useState({
    seguimiento_ck: '',
    check_number: '',
    fecha_ck: '',
    cantidad: '',
    plan: '',
    notas: '',
    facturador: '',
    status: 'pending'
  });
  const [savingChequeEdit, setSavingChequeEdit] = useState(false);
  const [showNewChequeModal, setShowNewChequeModal] = useState(false);
  const [savingCheque, setSavingCheque] = useState(false);
  const [newChequeData, setNewChequeData] = useState({
    seguimiento_ck: '',
    check_number: '',
    fecha_ck: '',
    cantidad: '',
    plan: '',
    notas: '',
    facturador: '',
    status: 'pending'
  });
  const [newChequeFacturas, setNewChequeFacturas] = useState<{numero: string; notas: string}[]>([]);
  const [newChequeFacturaInput, setNewChequeFacturaInput] = useState({ numero: '', notas: '' });

  // Client Log state
  interface ClientLog {
    id: number;
    client_id: number;
    facturado_desde: string | null;
    facturado_hasta: string | null;
    facturador: string | null;
    notas: string | null;
    created_at: string;
    created_by: number | null;
    created_by_first_name: string | null;
    created_by_last_name: string | null;
  }
  const [clientLogs, setClientLogs] = useState<ClientLog[]>([]);
  const [loadingClientLogs, setLoadingClientLogs] = useState(false);
  const [addingClientLog, setAddingClientLog] = useState(false);
  const [showAddLogForm, setShowAddLogForm] = useState(false);
  const [newLogData, setNewLogData] = useState({
    facturado_desde: '',
    facturado_hasta: '',
    facturador: '',
    notas: ''
  });

  // Invoice Detail Modal state
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceComments, setInvoiceComments] = useState<Comment[]>([]);
  const [newInvoiceComment, setNewInvoiceComment] = useState('');
  const [invoiceGestion, setInvoiceGestion] = useState('');
  const [isAddingInvoiceGestion, setIsAddingInvoiceGestion] = useState(false);
  const [isSavingInvoiceGestion, setIsSavingInvoiceGestion] = useState(false);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [invoiceEditData, setInvoiceEditData] = useState<Partial<Invoice>>({});
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editLogNotes, setEditLogNotes] = useState('');
  const [isSavingLogNotes, setIsSavingLogNotes] = useState(false);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);
  
  // PDF Analysis state
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [analyzingPdfs, setAnalyzingPdfs] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [reportType, setReportType] = useState<'facturado' | 'aging' | 'cheques'>('facturado');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [pdfResults, setPdfResults] = useState<{
    files: Array<{
      fileName: string;
      totalFacturado: number | null;
      totalDeducible: number | null;
      totalNeto: number | null;
      totalPagado: number | null;
      totalPendiente: number | null;
      numFacturas: number | null;
      dateFrom: string | null;
      dateTo: string | null;
    }>;
    grandTotal: number;
    grandTotalNeto: number;
    grandTotalDeducible: number;
    grandTotalCobrado: number;
    grandTotalPendiente: number;
    grandNumFacturas: number;
    allPlanes: Array<{
      planName: string;
      numFacturas: number;
      facturado: number;
      deducible: number;
      cobrado: number;
      neto: number;
    }>;
    totalFiles: number;
    successfulFiles: number;
  } | null>(null);
  const [savedReports, setSavedReports] = useState<Array<{
    id: number;
    original_name: string;
    report_type: string;
    date_from: string;
    date_to: string;
    num_facturas: number;
    total_facturado: number;
    total_deducible: number;
    total_cobrado: number;
    total_neto: number;
    plans_data: any;
    created_at: string;
    uploaded_by_first_name: string;
    uploaded_by_last_name: string;
  }>>([]);
  const [loadingSavedReports, setLoadingSavedReports] = useState(false);
  const [savedReportsTab, setSavedReportsTab] = useState<'all' | 'facturado' | 'aging' | 'cheques'>('all');
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const fetchClients = async () => {
    try {
      const response = await clientService.getAll();
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Auto-select client from URL parameter
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && clients.length > 0) {
      const client = clients.find(c => c.id === parseInt(selectedId));
      if (client && (!selectedClient || selectedClient.id !== client.id)) {
        selectClient(client);
      }
    }
  }, [searchParams, clients]);

  // Auto-open invoice detail modal from URL parameter
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId && clientInvoices.length > 0 && !loadingInvoices) {
      const invoice = clientInvoices.find(inv => inv.id === parseInt(invoiceId));
      if (invoice) {
        openInvoiceDetailModal(invoice);
        // Clear the param so it doesn't re-open on every render
        searchParams.delete('invoice');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [clientInvoices, loadingInvoices]);

  const selectClient = async (client: Client) => {
    setSelectedClient(client);
    setActiveTab('invoices');
    setLoadingInvoices(true);
    setLoadingDocuments(true);
    setLoadingSavedReports(true);
    setLoadingCheques(true);
    setLoadingClientLogs(true);
    setPdfResults(null);
    setPdfFiles([]);
    try {
      const [invoicesRes, documentsRes, reportsRes, chequesRes, logsRes] = await Promise.all([
        clientService.getInvoices(client.id),
        documentService.getByClient(client.id),
        reportService.getByClient(client.id),
        chequeService.getByClient(client.id),
        clientLogService.getByClient(client.id)
      ]);
      setClientInvoices(invoicesRes.data);
      setClientDocuments(documentsRes.data);
      setSavedReports(reportsRes.data);
      setClientCheques(chequesRes.data);
      setClientLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching client data:', error);
      setClientInvoices([]);
      setClientDocuments([]);
      setSavedReports([]);
      setClientCheques([]);
      setClientLogs([]);
    } finally {
      setLoadingInvoices(false);
      setLoadingDocuments(false);
      setLoadingSavedReports(false);
      setLoadingCheques(false);
      setLoadingClientLogs(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadDate(new Date().toISOString().split('T')[0]);
      setUploadDescription('');
      setShowUploadModal(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !selectedClient) return;

    setUploadingDocument(true);
    try {
      await documentService.upload(selectedClient.id, uploadFile, uploadDate, uploadDescription);
      const response = await documentService.getByClient(selectedClient.id);
      setClientDocuments(response.data);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDate('');
      setUploadDescription('');
    } catch (error: any) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.error || 'Error al subir el documento');
    } finally {
      setUploadingDocument(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadDate('');
    setUploadDescription('');
  };

  const handlePreview = async (doc: ClientDocument) => {
    setPreviewDocument(doc);
    setLoadingPreview(true);
    setShowPreviewModal(true);
    
    try {
      const response = await documentService.preview(doc.id);
      const blob = new Blob([response.data], { 
        type: doc.file_type === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      
      if (doc.file_type === 'pdf') {
        const url = URL.createObjectURL(blob);
        setPreviewContent(url);
        setCsvData([]);
      } else {
        const text = await blob.text();
        const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim().replace(/^"|"$/g, '')));
        setCsvData(rows.filter(row => row.some(cell => cell)));
        setPreviewContent('');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      alert('Error al cargar la vista previa');
      setShowPreviewModal(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreviewModal = () => {
    if (previewContent && previewDocument?.file_type === 'pdf') {
      URL.revokeObjectURL(previewContent);
    }
    setShowPreviewModal(false);
    setPreviewDocument(null);
    setPreviewContent('');
    setCsvData([]);
  };

  const handleImportFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
      setShowImportModal(true);
    }
    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile || !selectedClient) return;

    setImporting(true);
    try {
      const response = await invoiceService.importCsv(selectedClient.id, importFile);
      setImportResult(response.data);
      // Refresh invoices list
      const invoicesRes = await clientService.getInvoices(selectedClient.id);
      setClientInvoices(invoicesRes.data);
    } catch (error: any) {
      console.error('Error importing invoices:', error);
      setImportResult({
        imported: 0,
        errors: [error.response?.data?.error || 'Error al importar facturas']
      });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleDeleteAllInvoices = async () => {
    if (!selectedClient) return;
    setDeletingAll(true);
    try {
      const response = await invoiceService.deleteAllByClient(selectedClient.id);
      alert(`${response.data.deleted} facturas eliminadas`);
      setClientInvoices([]);
      setSelectedInvoices([]);
      setShowDeleteAllModal(false);
    } catch (error) {
      console.error('Error deleting invoices:', error);
      alert('Error al eliminar las facturas');
    } finally {
      setDeletingAll(false);
    }
  };

  const toggleInvoiceSelection = (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    const activeInvoices = clientInvoices.filter(inv => !['paid', 'refacturar', 'denied'].includes(inv.status));
    if (selectedInvoices.length === activeInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(activeInvoices.map(inv => inv.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedInvoices.length === 0) return;
    setDeletingSelected(true);
    try {
      const response = await invoiceService.deleteMultiple(selectedInvoices);
      alert(`${response.data.deleted} facturas eliminadas`);
      setClientInvoices(prev => prev.filter(inv => !selectedInvoices.includes(inv.id)));
      setSelectedInvoices([]);
      setShowDeleteSelectedModal(false);
    } catch (error) {
      console.error('Error deleting invoices:', error);
      alert('Error al eliminar las facturas');
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleAnalyzePdfs = async () => {
    if (pdfFiles.length === 0) return;
    setAnalyzingPdfs(true);
    setPdfResults(null);

    try {
      const response = await reportService.analyzePdfs(pdfFiles);
      setPdfResults(response.data);
      
      // Auto-detect report type based on filename
      const fileName = pdfFiles[0].name.toLowerCase();
      if (fileName.includes('aging')) {
        setReportType('aging');
      } else if (fileName.includes('cheque')) {
        setReportType('cheques');
      } else {
        setReportType('facturado');
      }
    } catch (error: any) {
      console.error('Error analyzing PDFs:', error);
      alert(error.response?.data?.error || 'Error al analizar PDFs');
    } finally {
      setAnalyzingPdfs(false);
    }
  };

  const handleSaveReport = async () => {
    if (!selectedClient || !pdfFiles[0] || !pdfResults) return;
    setSavingReport(true);

    try {
      const firstFile = pdfResults.files[0];
      // Use manual dates if provided, otherwise use extracted dates
      const dateFrom = reportDateFrom || firstFile?.dateFrom || undefined;
      const dateTo = reportDateTo || firstFile?.dateTo || undefined;
      
      await reportService.save(selectedClient.id, pdfFiles[0], {
        reportType,
        dateFrom,
        dateTo,
        numFacturas: pdfResults.grandNumFacturas,
        totalFacturado: pdfResults.grandTotal,
        totalDeducible: pdfResults.grandTotalDeducible,
        totalCobrado: pdfResults.grandTotalCobrado,
        totalNeto: pdfResults.grandTotalNeto,
        plansData: pdfResults.allPlanes,
      });
      
      alert('Reporte guardado correctamente');
      
      // Refresh saved reports
      const reportsRes = await reportService.getByClient(selectedClient.id);
      setSavedReports(reportsRes.data);
      
      // Clear current analysis
      setPdfResults(null);
      setPdfFiles([]);
      setReportType('facturado');
      setReportDateFrom('');
      setReportDateTo('');
    } catch (error: any) {
      console.error('Error saving report:', error);
      alert(error.response?.data?.error || 'Error al guardar reporte');
    } finally {
      setSavingReport(false);
    }
  };

  const handleDeleteReport = async (reportId: number) => {
    if (!window.confirm('¿Eliminar este reporte?')) return;
    
    try {
      await reportService.delete(reportId);
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Error al eliminar reporte');
    }
  };

  const handleDownload = async (doc: ClientDocument) => {
    try {
      const response = await documentService.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error al descargar el documento');
    }
  };

  const handleDeleteDocument = async (doc: ClientDocument) => {
    if (!window.confirm(`¿Eliminar "${doc.original_name}"?`)) return;
    
    try {
      await documentService.delete(doc.id);
      setClientDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error al eliminar el documento');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Invoice Detail Modal handlers
  const openInvoiceDetailModal = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailModal(true);
    try {
      const commentsRes = await commentService.getByInvoice(invoice.id);
      setInvoiceComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const closeInvoiceDetailModal = () => {
    setShowInvoiceDetailModal(false);
    setSelectedInvoice(null);
    setInvoiceComments([]);
    setNewInvoiceComment('');
    setInvoiceGestion('');
    setIsAddingInvoiceGestion(false);
    setIsEditingInvoice(false);
    setInvoiceEditData({});
  };

  const handleSaveLogNotes = async (logId: number) => {
    setIsSavingLogNotes(true);
    try {
      await clientLogService.update(logId, { notas: editLogNotes });
      setClientLogs(prev => prev.map(l => l.id === logId ? { ...l, notas: editLogNotes } : l));
      setEditingLogId(null);
      setEditLogNotes('');
    } catch (error) {
      console.error('Error saving log notes:', error);
    } finally {
      setIsSavingLogNotes(false);
    }
  };

  const handleAddInvoiceComment = async () => {
    if (!newInvoiceComment.trim() || !selectedInvoice) return;
    try {
      const response = await commentService.create({
        invoiceId: selectedInvoice.id,
        comment: newInvoiceComment
      });
      setInvoiceComments(prev => [response.data, ...prev]);
      setNewInvoiceComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleSaveInvoiceGestion = async () => {
    if (!selectedInvoice) return;
    setIsSavingInvoiceGestion(true);
    try {
      const now = new Date();
      const dateTime = `${now.toLocaleDateString('en-US')} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      const newEntry = `[${dateTime} - ${userName}]: ${invoiceGestion}`;
      const newGestion = selectedInvoice.management_notes 
        ? `${selectedInvoice.management_notes}\n\n${newEntry}`
        : newEntry;
      
      await invoiceService.update(selectedInvoice.id, { managementNotes: newGestion });
      const updatedInvoice = { ...selectedInvoice, management_notes: newGestion };
      setSelectedInvoice(updatedInvoice);
      setClientInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
      setInvoiceGestion('');
      setIsAddingInvoiceGestion(false);
    } catch (error) {
      console.error('Error saving gestion:', error);
    } finally {
      setIsSavingInvoiceGestion(false);
    }
  };

  const handleInvoiceStatusChange = async (newStatus: Invoice['status']) => {
    if (!selectedInvoice) return;
    try {
      await invoiceService.update(selectedInvoice.id, { status: newStatus });
      const updatedInvoice = { ...selectedInvoice, status: newStatus };
      setSelectedInvoice(updatedInvoice);
      setClientInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const startEditingInvoice = async () => {
    if (selectedInvoice) {
      setInvoiceEditData({
        invoice_number: selectedInvoice.invoice_number,
        plan_code: selectedInvoice.plan_code,
        amount: selectedInvoice.amount,
        invoice_date: selectedInvoice.invoice_date ? selectedInvoice.invoice_date.split('T')[0] : '',
        service_date: selectedInvoice.service_date ? selectedInvoice.service_date.split('T')[0] : '',
        pending_notes: selectedInvoice.pending_notes,
        status: selectedInvoice.status,
        initials: selectedInvoice.initials
      });
      try {
        const res = await planService.getAll();
        setBillingPlans(res.data);
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
      setIsEditingInvoice(true);
    }
  };

  const handleSaveInvoiceEdit = async () => {
    if (!selectedInvoice) return;
    setIsSavingInvoice(true);
    try {
      await invoiceService.update(selectedInvoice.id, invoiceEditData);
      const updatedInvoice = { ...selectedInvoice, ...invoiceEditData };
      setSelectedInvoice(updatedInvoice);
      setClientInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
      setIsEditingInvoice(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    if (!window.confirm(`¿Eliminar factura #${selectedInvoice.invoice_number}?`)) return;
    setIsDeletingInvoice(true);
    try {
      await invoiceService.delete(selectedInvoice.id);
      setClientInvoices(prev => prev.filter(inv => inv.id !== selectedInvoice.id));
      closeInvoiceDetailModal();
    } catch (error) {
      console.error('Error deleting invoice:', error);
    } finally {
      setIsDeletingInvoice(false);
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_process: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      paid: 'bg-purple-100 text-purple-800',
      refacturar: 'bg-orange-100 text-orange-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const invoiceStatusLabels: Record<string, string> = {
    pending: 'Pendiente',
    in_process: 'En Proceso',
    completed: 'Completada',
    denied: 'Denegada',
    paid: 'Pagada',
    refacturar: 'Re-Facturar',
    ajuste: 'Ajuste',
  };

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredClients(clients.filter(c => 
        c.company_name?.toLowerCase().includes(term) ||
        c.first_name?.toLowerCase().includes(term) ||
        c.last_name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);

  const openNewClientModal = () => {
    setEditingClient(null);
    setFormData(emptyFormData);
    setError('');
    setShowModal(true);
  };

  const openEditClientModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      email: client.email || '',
      password: '',
      firstName: client.first_name || '',
      lastName: client.last_name || '',
      initials: '',
      companyName: client.company_name || '',
      phone: client.phone || '',
      address: client.address || '',
      taxId: client.tax_id || ''
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setFormData(emptyFormData);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      if (editingClient) {
        await clientService.update(editingClient.id, {
          companyName: formData.companyName,
          phone: formData.phone,
          address: formData.address,
          taxId: formData.taxId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email
        });
      } else {
        await clientService.create(formData);
      }
      await fetchClients();
      if (editingClient && selectedClient && editingClient.id === selectedClient.id) {
        setSelectedClient({
          ...selectedClient,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company_name: formData.companyName,
          phone: formData.phone,
          address: formData.address,
          tax_id: formData.taxId
        });
      }
      closeModal();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el cliente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-56px)] gap-4">
      {/* Sidebar izquierdo - Lista de clientes */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl shadow flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-bold text-gray-800">Clientes</h2>
            <button
              onClick={openNewClientModal}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
              title="Nuevo Cliente"
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              onClick={() => selectClient(client)}
              className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition ${
                selectedClient?.id === client.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="text-blue-600" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-800 truncate">
                    {client.first_name} {client.last_name}
                  </h3>
                  {client.company_name && (
                    <p className="text-xs text-gray-500 truncate">{client.company_name}</p>
                  )}
                </div>
                {(client as any).invoice_count > 0 && (
                  <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 flex-shrink-0">
                    {(client as any).invoice_count}
                  </span>
                )}
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </div>
          ))}
          {filteredClients.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No se encontraron clientes
            </div>
          )}
        </div>
      </div>

      {/* Panel derecho - Información del cliente y facturas */}
      <div className="flex-1 bg-white rounded-xl shadow overflow-hidden flex flex-col">
        {selectedClient ? (
          <>
            {/* Información del cliente */}
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="text-blue-600" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-gray-800 truncate">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </h2>
                    {selectedClient.company_name && (
                      <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                        <Building2 size={12} />
                        {selectedClient.company_name}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-[10px] rounded-full flex-shrink-0 ${
                      selectedClient.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {selectedClient.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1.5" title={selectedClient.email}>
                      <Mail size={12} className="text-gray-400" />
                      <span className="truncate max-w-[180px]">{selectedClient.email}</span>
                    </span>
                    {selectedClient.phone && (
                      <span className="flex items-center gap-1.5">
                        <Phone size={12} className="text-gray-400" />
                        {selectedClient.phone}
                      </span>
                    )}
                    {selectedClient.tax_id && (
                      <span className="flex items-center gap-1.5">
                        <CreditCard size={12} className="text-gray-400" />
                        {selectedClient.tax_id}
                      </span>
                    )}
                    {selectedClient.address && (
                      <span className="flex items-center gap-1.5" title={selectedClient.address}>
                        <MapPin size={12} className="text-gray-400" />
                        <span className="truncate max-w-[250px]">{selectedClient.address}</span>
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => openEditClientModal(selectedClient)}
                  className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition flex-shrink-0"
                  title="Editar Cliente"
                >
                  <Edit2 size={18} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'invoices'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileText size={16} className="inline mr-2" />
                  Facturas ({clientInvoices.length})
                </button>
                <button
                  onClick={() => setActiveTab('cheques')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'cheques'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Banknote size={16} className="inline mr-2" />
                  Cheques ({clientCheques.length})
                </button>
                <button
                  onClick={() => setActiveTab('client_log')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'client_log'
                      ? 'border-amber-600 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BookOpen size={16} className="inline mr-2" />
                  Notas Generales ({clientLogs.length})
                </button>
                <button
                  onClick={() => setActiveTab('docs_reports')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition ${
                    activeTab === 'docs_reports'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <File size={16} className="inline mr-2" />
                  Docs y Reportes
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === 'invoices' ? (
                <>
                  {/* Invoices Header */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Log de Facturas</h3>
                    <p className="text-gray-500 text-sm mb-3">Gestiona y da seguimiento a las facturas del cliente. Importa desde CSV o agrega manualmente.</p>
                  </div>

                  {/* Search, Filter, and Import */}
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="text"
                          placeholder="Buscar # factura..."
                          value={invoiceSearch}
                          onChange={(e) => setInvoiceSearch(e.target.value)}
                          className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-44"
                        />
                      </div>
                      <select
                        value={invoiceStatusFilter}
                        onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">Todos los estados</option>
                        <option value="pending">Pendiente</option>
                        <option value="in_process">En Proceso</option>
                        <option value="completed">Completado</option>
                        <option value="refacturar">Re-Facturar</option>
                        <option value="paid">Pagada</option>
                        <option value="denied">Denegado</option>
                        <option value="ajuste">Ajuste</option>
                      </select>
                      {selectedInvoices.length > 0 && (
                        <button
                          onClick={() => setShowDeleteSelectedModal(true)}
                          className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition"
                        >
                          <Trash2 size={16} />
                          Eliminar ({selectedInvoices.length})
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={importInputRef}
                        onChange={handleImportFileSelect}
                        accept=".csv"
                        className="hidden"
                      />
                      <button
                        onClick={() => importInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                      >
                        <FileUp size={16} />
                        Importar
                      </button>
                      <button
                        onClick={async () => {
                          const autoInitials = user?.firstName && user?.lastName ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() : '';
                          setNewInvoiceData({
                            invoiceNumber: '',
                            planId: '',
                            serviceDate: '',
                            invoiceDate: new Date().toISOString().split('T')[0],
                            amount: '',
                            status: 'pending',
                            pendingNotes: '',
                            managementNotes: '',
                            initials: autoInitials
                          });
                          setNewInvoiceError('');
                          try {
                            const res = await planService.getAll();
                            setBillingPlans(res.data);
                          } catch (err) {
                            console.error('Error fetching plans:', err);
                          }
                          setShowNewInvoiceModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                      >
                        <Plus size={16} />
                        Nueva Factura
                      </button>
                    </div>
                  </div>

                  {/* Invoices Table */}
                  {loadingInvoices ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : clientInvoices
                      .filter(inv => invoiceStatusFilter === 'all' || inv.status === invoiceStatusFilter)
                      .filter(inv => !invoiceSearch || inv.invoice_number.toLowerCase().includes(invoiceSearch.toLowerCase()))
                      .length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600"># Factura</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Plan</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Monto</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Fecha Servicio</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Fecha Factura</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Estado</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Facturador</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {(() => {
                            const filtered = clientInvoices
                              .filter(inv => invoiceStatusFilter === 'all' || inv.status === invoiceStatusFilter)
                              .filter(inv => !invoiceSearch || inv.invoice_number.toLowerCase().includes(invoiceSearch.toLowerCase()));
                            const pending = filtered.filter(inv => !['completed', 'paid'].includes(inv.status));
                            const completed = filtered.filter(inv => ['completed', 'paid'].includes(inv.status));
                            const renderRow = (invoice: any) => {
                              const status = statusColors[invoice.status] || statusColors.pending;
                              return (
                                <tr key={invoice.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => openInvoiceDetailModal(invoice)}>
                                  <td className="px-3 py-2 font-mono font-medium">{invoice.invoice_number}</td>
                                  <td className="px-3 py-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      {invoice.plan_code || invoice.plan_name || '-'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 font-medium">{formatCurrency(invoice.amount)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(invoice.service_date)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(invoice.invoice_date)}</td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${status.bg} ${status.text}`}>
                                      {status.label}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600">{invoice.initials || '-'}</td>
                                </tr>
                              );
                            };
                            return (
                              <>
                                {pending.map(renderRow)}
                                {pending.length > 0 && completed.length > 0 && (
                                  <tr>
                                    <td colSpan={7} className="px-3 py-4">
                                      <div className="flex items-center gap-3">
                                        <div className="flex-1 border-t border-emerald-300" />
                                        <span className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">Completadas / Pagadas</span>
                                        <div className="flex-1 border-t border-emerald-300" />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {completed.map(renderRow)}
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <FileText size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>No hay facturas</p>
                    </div>
                  )}
                </>
              ) : activeTab === 'docs_reports' ? (
                <>
                  {/* Sub-tabs for Documents and Reports */}
                  <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
                    <button
                      onClick={() => setDocsReportsSubTab('documents')}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                        docsReportsSubTab === 'documents'
                          ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <File size={16} className="inline mr-2" />
                      Documentos ({clientDocuments.length})
                    </button>
                    <button
                      onClick={() => setDocsReportsSubTab('reports')}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                        docsReportsSubTab === 'reports'
                          ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <BarChart3 size={16} className="inline mr-2" />
                      Reportes
                    </button>
                  </div>

                  {docsReportsSubTab === 'documents' ? (
                <>
                  {/* Documents Section */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Documentos del Cliente</h3>
                    <p className="text-gray-500 text-sm mb-3">Sube y gestiona documentos importantes del cliente como contratos, acuerdos y archivos de referencia.</p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.csv"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                    >
                      <Upload size={18} />
                      Subir Documento (PDF, CSV)
                    </button>
                  </div>

                  {loadingDocuments ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : clientDocuments.length > 0 ? (
                    <div className="space-y-3">
                      {clientDocuments.map((doc) => (
                        <div
                          key={doc.id}
                          className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                doc.file_type === 'pdf' ? 'bg-red-100' : 'bg-green-100'
                              }`}>
                                <File size={20} className={doc.file_type === 'pdf' ? 'text-red-600' : 'text-green-600'} />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">{doc.original_name}</p>
                                {doc.description && (
                                  <p className="text-sm text-gray-600 mt-0.5">{doc.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatFileSize(doc.file_size)} • 
                                  {doc.document_date && <span className="font-medium"> Fecha: {formatDate(doc.document_date)} • </span>}
                                  Subido: {formatDate(doc.created_at)} por {doc.uploaded_by_first_name} {doc.uploaded_by_last_name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handlePreview(doc)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                                title="Ver"
                              >
                                <Eye size={18} />
                              </button>
                              <button
                                onClick={() => handleDownload(doc)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="Descargar"
                              >
                                <Download size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Eliminar"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <File size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>Este cliente no tiene documentos</p>
                      <p className="text-sm mt-1">Sube archivos PDF o CSV</p>
                    </div>
                  )}
                  </>
                  ) : (
                  <>
                  {/* PDF/CSV Analysis */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Analizar Reportes (PDF o CSV)</h3>
                    <p className="text-gray-500 text-sm mb-3">Sube archivos PDF o CSV de reportes de facturación para extraer los totales automáticamente.</p>
                    
                    <div className="flex items-center gap-4">
                      <input
                        ref={pdfInputRef}
                        type="file"
                        accept=".pdf,.csv"
                        multiple
                        onChange={(e) => setPdfFiles(Array.from(e.target.files || []))}
                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {pdfFiles.length > 0 && (
                        <span className="text-sm text-gray-600">{pdfFiles.length} archivo(s)</span>
                      )}
                      <button
                        onClick={handleAnalyzePdfs}
                        disabled={pdfFiles.length === 0 || analyzingPdfs}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                      >
                        <BarChart3 size={18} />
                        {analyzingPdfs ? 'Analizando...' : 'Analizar Archivos'}
                      </button>
                    </div>
                  </div>

                  {/* Results */}
                  {pdfResults && (
                    <div className="space-y-6">
                      {/* Save Report Section - At Top */}
                      <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
                        <h4 className="font-semibold text-gray-800">Guardar Reporte en Cliente</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Reporte</label>
                            <select
                              value={reportType}
                              onChange={(e) => setReportType(e.target.value as 'facturado' | 'aging' | 'cheques')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="facturado">Total Facturado</option>
                              <option value="aging">Aging</option>
                              <option value="cheques">Cheques Registrados</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Desde</label>
                            <input
                              type="date"
                              value={reportDateFrom}
                              onChange={(e) => setReportDateFrom(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Hasta</label>
                            <input
                              type="date"
                              value={reportDateTo}
                              onChange={(e) => setReportDateTo(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          
                          <div className="flex items-end">
                            <button
                              onClick={handleSaveReport}
                              disabled={savingReport}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50"
                            >
                              <Save size={18} />
                              {savingReport ? 'Guardando...' : 'Guardar'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <div className="bg-blue-500 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-90"># Facturas</p>
                          <p className="text-xl font-bold">{pdfResults.grandNumFacturas || 0}</p>
                        </div>
                        <div className="bg-green-500 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-90">Facturado</p>
                          <p className="text-xl font-bold">{formatCurrency(pdfResults.grandTotal)}</p>
                        </div>
                        <div className="bg-orange-500 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-90">Deducible</p>
                          <p className="text-xl font-bold">{formatCurrency(pdfResults.grandTotalDeducible)}</p>
                        </div>
                        <div className="bg-purple-500 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-90">Neto</p>
                          <p className="text-xl font-bold">{formatCurrency(pdfResults.grandTotalNeto)}</p>
                        </div>
                        {pdfResults.grandTotalCobrado > 0 && (
                          <div className="bg-teal-500 rounded-lg p-3 text-white">
                            <p className="text-xs opacity-90">Cobrado</p>
                            <p className="text-xl font-bold">{formatCurrency(pdfResults.grandTotalCobrado)}</p>
                          </div>
                        )}
                        {pdfResults.grandTotalPendiente !== 0 && pdfResults.grandTotalPendiente !== undefined && (
                          <div className="bg-red-500 rounded-lg p-3 text-white">
                            <p className="text-xs opacity-90">Diferencia</p>
                            <p className="text-xl font-bold">{formatCurrency(pdfResults.grandTotalPendiente)}</p>
                          </div>
                        )}
                      </div>

                      {/* Plans Table */}
                      {pdfResults.allPlanes && pdfResults.allPlanes.length > 0 && (
                        <div className="bg-white border rounded-lg overflow-hidden">
                          <div className="px-4 py-2 bg-gray-50 border-b">
                            <h4 className="font-semibold text-gray-800">Totales por Plan</h4>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Plan</th>
                                  <th className="px-3 py-2 text-right">#</th>
                                  <th className="px-3 py-2 text-right">Facturado</th>
                                  <th className="px-3 py-2 text-right">Deducible</th>
                                  <th className="px-3 py-2 text-right">Cobrado</th>
                                  <th className="px-3 py-2 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {pdfResults.allPlanes.map((plan, i) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 font-medium">{plan.planName}</td>
                                    <td className="px-3 py-2 text-right">{plan.numFacturas}</td>
                                    <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(plan.facturado)}</td>
                                    <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(plan.deducible)}</td>
                                    <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(plan.cobrado)}</td>
                                    <td className="px-3 py-2 text-right text-green-600 font-semibold">{formatCurrency(plan.neto)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Saved Reports - Always visible */}
                  <div className="mt-8 border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Reportes Guardados</h3>
                        
                        {/* Report Type Tabs */}
                        <div className="flex gap-2 mb-4">
                          <button
                            onClick={() => setSavedReportsTab('all')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition ${savedReportsTab === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Todos ({savedReports.length})
                          </button>
                          <button
                            onClick={() => setSavedReportsTab('facturado')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition ${savedReportsTab === 'facturado' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Facturado ({savedReports.filter(r => r.report_type === 'facturado').length})
                          </button>
                          <button
                            onClick={() => setSavedReportsTab('aging')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition ${savedReportsTab === 'aging' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Aging ({savedReports.filter(r => r.report_type === 'aging').length})
                          </button>
                          <button
                            onClick={() => setSavedReportsTab('cheques')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition ${savedReportsTab === 'cheques' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Cheques ({savedReports.filter(r => r.report_type === 'cheques').length})
                          </button>
                        </div>
                        
                        {loadingSavedReports ? (
                          <div className="flex justify-center py-8">
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : savedReports.filter(r => savedReportsTab === 'all' || r.report_type === savedReportsTab).length > 0 ? (
                          <div className="space-y-3">
                            {savedReports.filter(r => savedReportsTab === 'all' || r.report_type === savedReportsTab).map(report => (
                              <div key={report.id} className={`bg-white border rounded-lg p-4 ${report.report_type === 'cheques' ? 'border-l-4 border-l-teal-500' : report.report_type === 'aging' ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-green-500'}`}>
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-800">{report.original_name}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded ${report.report_type === 'cheques' ? 'bg-teal-100 text-teal-700' : report.report_type === 'aging' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                        {report.report_type === 'cheques' ? 'Cheques' : report.report_type === 'aging' ? 'Aging' : 'Facturado'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                      Período: {report.date_from ? new Date(report.date_from).toLocaleDateString('en-US') : '-'} - {report.date_to ? new Date(report.date_to).toLocaleDateString('en-US') : '-'}
                                    </p>
                                    <div className="flex gap-4 mt-2 text-sm">
                                      <span><strong>{report.num_facturas}</strong> facturas</span>
                                      <span className="text-green-600"><strong>{formatCurrency(report.total_facturado)}</strong> facturado</span>
                                      {report.report_type === 'cheques' && report.total_cobrado > 0 && (
                                        <span className="text-teal-600"><strong>{formatCurrency(report.total_cobrado)}</strong> cobrado</span>
                                      )}
                                      <span className="text-purple-600"><strong>{formatCurrency(report.total_neto)}</strong> neto</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                      Subido: {new Date(report.created_at).toLocaleDateString('en-US')} por {report.uploaded_by_first_name} {report.uploaded_by_last_name}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteReport(report.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">No hay reportes guardados</p>
                        )}
                  </div>
                  </>
                  )}
                </>
              ) : activeTab === 'cheques' ? (
                <>
                  {/* Cheques Header */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Log de Cheques</h3>
                    <p className="text-gray-500 text-sm mb-3">Gestiona y da seguimiento a los cheques del cliente. Importa desde CSV o agrega manualmente.</p>
                  </div>

                  {/* Search and Import */}
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="text"
                          placeholder="Buscar # cheque..."
                          value={chequeSearch}
                          onChange={(e) => setChequeSearch(e.target.value)}
                          className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 w-44"
                        />
                      </div>
                      <select
                        value={chequeStatusFilter}
                        onChange={(e) => setChequeStatusFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      >
                        <option value="all">Todos los estados</option>
                        <option value="pending">Pendiente</option>
                        <option value="in_process">En Proceso</option>
                        <option value="completed">Completado</option>
                        <option value="denied">Denegado</option>
                        <option value="paid">Pagado</option>
                        <option value="ajuste">Ajuste</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={chequeImportRef}
                        accept=".xlsx,.xls,.csv"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedClient) {
                            setImportingCheques(true);
                            setChequeImportResult(null);
                            try {
                              const response = await chequeService.importCsv(selectedClient.id, file);
                              setChequeImportResult(response.data);
                              const chequesRes = await chequeService.getByClient(selectedClient.id);
                              setClientCheques(chequesRes.data);
                            } catch (error) {
                              console.error('Error importing cheques:', error);
                              setChequeImportResult({ imported: 0, errors: ['Error al importar el archivo'] });
                            } finally {
                              setImportingCheques(false);
                            }
                          }
                          if (chequeImportRef.current) chequeImportRef.current.value = '';
                        }}
                        className="hidden"
                      />
                      <button
                        onClick={() => chequeImportRef.current?.click()}
                        disabled={importingCheques}
                        className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                      >
                        <FileUp size={16} />
                        {importingCheques ? 'Importando...' : 'Importar'}
                      </button>
                      <button
                        onClick={async () => {
                          const autoFact = user?.firstName && user?.lastName ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() : '';
                          setNewChequeData({
                            seguimiento_ck: '',
                            check_number: '',
                            fecha_ck: new Date().toISOString().split('T')[0],
                            cantidad: '',
                            plan: '',
                            notas: '',
                            facturador: autoFact,
                            status: 'pending'
                          });
                          try {
                            const res = await planService.getAll();
                            setBillingPlans(res.data);
                          } catch (err) {
                            console.error('Error fetching plans:', err);
                          }
                          setShowNewChequeModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                      >
                        <Plus size={16} />
                        Nuevo Cheque
                      </button>
                    </div>
                  </div>

                  {/* Import Result */}
                  {chequeImportResult && (
                    <div className={`mb-4 p-3 rounded-lg ${chequeImportResult.errors.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                      <p className="text-sm font-medium">
                        {chequeImportResult.imported} cheques importados
                        {chequeImportResult.errors.length > 0 && ` con ${chequeImportResult.errors.length} errores`}
                      </p>
                      {chequeImportResult.errors.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600">
                          {chequeImportResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Cheques List */}
                  {loadingCheques ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : clientCheques
                    .filter(c => chequeStatusFilter === 'all' || c.status === chequeStatusFilter)
                    .filter(c => 
                      !chequeSearch || 
                      c.check_number?.toLowerCase().includes(chequeSearch.toLowerCase()) ||
                      c.seguimiento_ck?.toLowerCase().includes(chequeSearch.toLowerCase()) ||
                      c.plan?.toLowerCase().includes(chequeSearch.toLowerCase())
                    ).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600"># Seguimiento</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600"># Cheque</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Fecha CK</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Plan</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Estado</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Facturador</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {(() => {
                            const filtered = clientCheques
                              .filter(c => chequeStatusFilter === 'all' || c.status === chequeStatusFilter)
                              .filter(c => 
                                !chequeSearch || 
                                c.check_number?.toLowerCase().includes(chequeSearch.toLowerCase()) ||
                                c.seguimiento_ck?.toLowerCase().includes(chequeSearch.toLowerCase()) ||
                                c.plan?.toLowerCase().includes(chequeSearch.toLowerCase())
                              );
                            const pendingCk = filtered.filter(c => !['completed', 'paid'].includes(c.status));
                            const completedCk = filtered.filter(c => ['completed', 'paid'].includes(c.status));
                            const renderChequeRow = (cheque: any) => {
                              const chequeStatus = statusColors[cheque.status] || statusColors.pending;
                              return (
                                <tr key={cheque.id} className="hover:bg-green-50 cursor-pointer" onClick={async () => {
                                  try {
                                    const res = await chequeService.getById(cheque.id);
                                    setSelectedCheque(res.data);
                                    setShowChequeLogModal(true);
                                  } catch (err) {
                                    console.error('Error fetching cheque:', err);
                                  }
                                }}>
                                  <td className="px-3 py-2">{cheque.seguimiento_ck || '-'}</td>
                                  <td className="px-3 py-2 font-mono">{cheque.check_number || '-'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {cheque.fecha_ck ? new Date(cheque.fecha_ck).toLocaleDateString('en-US') : '-'}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      {cheque.plan || '-'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${chequeStatus.bg} ${chequeStatus.text}`}>
                                      {chequeStatus.label}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600">{cheque.facturador || '-'}</td>
                                </tr>
                              );
                            };
                            return (
                              <>
                                {pendingCk.map(renderChequeRow)}
                                {pendingCk.length > 0 && completedCk.length > 0 && (
                                  <tr>
                                    <td colSpan={6} className="px-3 py-4">
                                      <div className="flex items-center gap-3">
                                        <div className="flex-1 border-t border-emerald-300" />
                                        <span className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">Completados / Pagados</span>
                                        <div className="flex-1 border-t border-emerald-300" />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {completedCk.map(renderChequeRow)}
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Banknote size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>No hay cheques registrados</p>
                      <p className="text-sm mt-1">Importa un CSV para comenzar</p>
                    </div>
                  )}
                </>
              ) : activeTab === 'client_log' ? (
                <>
                  {/* Client Log Header */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Notas Generales</h3>
                    <p className="text-gray-500 text-sm mb-3">Registro interno de facturación. Lleva el control de lo facturado por cada facturador.</p>
                  </div>

                  {/* Add Form + Button */}
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div className="flex-1" />
                    {!showAddLogForm && (
                      <button
                        onClick={() => {
                          setShowAddLogForm(true);
                          setNewLogData({
                            facturado_desde: '',
                            facturado_hasta: '',
                            facturador: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
                            notas: ''
                          });
                        }}
                        className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition text-sm font-medium"
                      >
                        <Plus size={18} />
                        Nueva Entrada
                      </button>
                    )}
                  </div>

                  {/* Add Log Form */}
                  {showAddLogForm && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-700 mb-3 text-sm">Nueva Entrada de Log</h4>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Facturado Desde</label>
                          <input
                            type="date"
                            value={newLogData.facturado_desde}
                            onChange={(e) => setNewLogData({ ...newLogData, facturado_desde: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Facturado Hasta</label>
                          <input
                            type="date"
                            value={newLogData.facturado_hasta}
                            onChange={(e) => setNewLogData({ ...newLogData, facturado_hasta: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
                          <input
                            type="text"
                            value={newLogData.notas}
                            onChange={(e) => setNewLogData({ ...newLogData, notas: e.target.value })}
                            placeholder="Notas para otros facturadores..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setShowAddLogForm(false)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-amber-100 rounded-lg transition"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={async () => {
                            if (!selectedClient) return;
                            setAddingClientLog(true);
                            try {
                              const facturador = user?.firstName && user?.lastName ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() : '';
                              await clientLogService.create({
                                client_id: selectedClient.id,
                                ...newLogData,
                                facturador
                              });
                              const res = await clientLogService.getByClient(selectedClient.id);
                              setClientLogs(res.data);
                              setShowAddLogForm(false);
                              setNewLogData({ facturado_desde: '', facturado_hasta: '', facturador: '', notas: '' });
                            } catch (err) {
                              console.error('Error adding client log:', err);
                            } finally {
                              setAddingClientLog(false);
                            }
                          }}
                          disabled={addingClientLog}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition text-sm disabled:opacity-50"
                        >
                          {addingClientLog ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Plus size={16} />
                          )}
                          Agregar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Log Table */}
                  {loadingClientLogs ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : clientLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Facturador</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Facturado Desde</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Facturado Hasta</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Notas</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Fecha</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-600"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {clientLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                  {log.facturador || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {log.facturado_desde ? new Date(log.facturado_desde).toLocaleDateString('en-US') : '-'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {log.facturado_hasta ? new Date(log.facturado_hasta).toLocaleDateString('en-US') : '-'}
                              </td>
                              <td className="px-3 py-2 max-w-[300px]">
                                {editingLogId === log.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editLogNotes}
                                      onChange={(e) => setEditLogNotes(e.target.value)}
                                      className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      autoFocus
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLogNotes(log.id); if (e.key === 'Escape') setEditingLogId(null); }}
                                    />
                                    <button onClick={() => handleSaveLogNotes(log.id)} disabled={isSavingLogNotes} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Guardar">
                                      <Save size={14} />
                                    </button>
                                    <button onClick={() => setEditingLogId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Cancelar">
                                      <X size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    className="truncate block cursor-pointer hover:text-blue-600"
                                    title={log.notas ? `${log.notas} (click para editar)` : 'Click para agregar nota'}
                                    onClick={() => { setEditingLogId(log.id); setEditLogNotes(log.notas || ''); }}
                                  >
                                    {log.notas || <span className="text-gray-300 italic">Click para editar</span>}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                {new Date(log.created_at).toLocaleDateString('en-US')}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {user?.role === 'admin' && (
                                  <button
                                    onClick={async () => {
                                      if (!confirm('¿Eliminar esta entrada del log?')) return;
                                      try {
                                        await clientLogService.delete(log.id);
                                        setClientLogs(prev => prev.filter(l => l.id !== log.id));
                                      } catch (err) {
                                        console.error('Error deleting log:', err);
                                      }
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <BookOpen size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>Sin entradas en el log</p>
                      <p className="text-sm mt-1">Agrega la primera entrada para comenzar</p>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Users size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Selecciona un cliente</p>
              <p className="text-sm">para ver su información y facturas</p>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Iniciales</label>
                  <input
                    type="text"
                    name="initials"
                    value={formData.initials}
                    onChange={handleChange}
                    maxLength={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / RNC</label>
                  <input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  {editingClient ? 'Actualizar' : 'Crear Cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Document Modal */}
      {showPreviewModal && previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  previewDocument.file_type === 'pdf' ? 'bg-red-100' : 'bg-green-100'
                }`}>
                  <File size={20} className={previewDocument.file_type === 'pdf' ? 'text-red-600' : 'text-green-600'} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{previewDocument.original_name}</h2>
                  {previewDocument.description && (
                    <p className="text-sm text-gray-500">{previewDocument.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewDocument)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm"
                >
                  <Download size={16} />
                  Descargar
                </button>
                <button onClick={closePreviewModal} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : previewDocument.file_type === 'pdf' ? (
                <iframe
                  src={previewContent}
                  className="w-full h-full border-0"
                  title={previewDocument.original_name}
                />
              ) : (
                <div className="overflow-auto h-full p-4">
                  <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 bg-gray-100">
                      {csvData.length > 0 && (
                        <tr>
                          {csvData[0].map((cell, i) => (
                            <th key={i} className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700">
                              {cell}
                            </th>
                          ))}
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {csvData.slice(1).map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="border border-gray-300 px-3 py-2 text-sm text-gray-600">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvData.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No hay datos para mostrar
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Subir Documento</h2>
              <button onClick={closeUploadModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {uploadFile && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    uploadFile.name.endsWith('.pdf') ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <File size={20} className={uploadFile.name.endsWith('.pdf') ? 'text-red-600' : 'text-green-600'} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{uploadFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(uploadFile.size)}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha del Documento *
                </label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Descripción del documento..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUploadSubmit}
                  disabled={uploadingDocument || !uploadDate}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {uploadingDocument ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload size={18} />
                  )}
                  Subir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Importar Facturas</h2>
              <button onClick={closeImportModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {importFile && !importResult && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                    <FileUp size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{importFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(importFile.size)}</p>
                  </div>
                </div>
              )}

              {!importResult && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium mb-2">Formato del CSV:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li><strong>factura/numero</strong> - Número de factura (requerido)</li>
                    <li><strong>fecha/date</strong> - Fecha de factura</li>
                    <li><strong>servicio/service</strong> - Fecha de servicio</li>
                    <li><strong>monto/amount</strong> - Monto</li>
                    <li><strong>plan</strong> - Código del plan</li>
                    <li><strong>estado/status</strong> - Estado</li>
                    <li><strong>pendiente</strong> - Notas pendientes</li>
                    <li><strong>gestion</strong> - Notas de gestión</li>
                    <li><strong>iniciales</strong> - Iniciales</li>
                  </ul>
                </div>
              )}

              {importResult && (
                <div className="space-y-3">
                  <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className={`font-medium ${importResult.imported > 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                      {importResult.imported} facturas importadas
                    </p>
                  </div>
                  
                  {importResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="font-medium text-red-800 mb-2">Errores ({importResult.errors.length}):</p>
                      <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {importResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeImportModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  {importResult ? 'Cerrar' : 'Cancelar'}
                </button>
                {!importResult && (
                  <button
                    onClick={handleImportSubmit}
                    disabled={importing}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50"
                  >
                    {importing ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FileUp size={18} />
                    )}
                    Importar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Invoices Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-red-600">⚠️ Eliminar Todas las Facturas</h2>
              <button onClick={() => setShowDeleteAllModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                <p className="font-medium">¿Estás seguro?</p>
                <p className="text-sm mt-2">
                  Esta acción eliminará <strong>{clientInvoices.length} facturas</strong> del cliente 
                  <strong> {selectedClient?.company_name || `${selectedClient?.first_name} ${selectedClient?.last_name}`}</strong>.
                </p>
                <p className="text-sm mt-2 font-medium">Esta acción no se puede deshacer.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowDeleteAllModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAllInvoices}
                  disabled={deletingAll}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {deletingAll ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                  Eliminar Todas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Selected Invoices Modal */}
      {showDeleteSelectedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-orange-600">Eliminar Facturas Seleccionadas</h2>
              <button onClick={() => setShowDeleteSelectedModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-orange-800">
                <p className="font-medium">¿Estás seguro?</p>
                <p className="text-sm mt-2">
                  Esta acción eliminará <strong>{selectedInvoices.length} facturas</strong> seleccionadas.
                </p>
                <p className="text-sm mt-2 font-medium">Esta acción no se puede deshacer.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowDeleteSelectedModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deletingSelected}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {deletingSelected ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                  Eliminar ({selectedInvoices.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cheque Detail Modal */}
      {showChequeLogModal && selectedCheque && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-200/50">
            {/* Header */}
            <div className="relative px-8 py-6 bg-white border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-600/20">
                    <Banknote size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900">
                        #{selectedCheque.check_number || selectedCheque.id}
                      </h2>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        {selectedCheque.plan || 'Sin Plan'}
                      </span>
                    </div>
                    <p className="text-gray-500 mt-1">
                      {selectedClient?.company_name || `${selectedClient?.first_name} ${selectedClient?.last_name}`}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowChequeLogModal(false);
                    setSelectedCheque(null);
                    setNewLogNote('');
                    setNewFacturaNumero('');
                    setNewFacturaNotas('');
                    setNewClientNota('');
                    setChequeDetailTab('facturas');
                  }} 
                  className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col lg:flex-row max-h-[calc(90vh-100px)]">
              {/* Left Panel - Main Content */}
              <div className="flex-1 p-6 overflow-y-auto bg-gray-50/50">
                {/* Key Info Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                        <Calendar size={14} className="text-green-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha CK</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{selectedCheque.fecha_ck ? new Date(selectedCheque.fecha_ck).toLocaleDateString('en-US') : '-'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText size={14} className="text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Seguimiento</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{selectedCheque.seguimiento_ck || '-'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center">
                        <User size={14} className="text-purple-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Facturador</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{selectedCheque.facturador || '-'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FileText size={14} className="text-orange-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Facturas</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{selectedCheque.facturas?.length || 0}</p>
                  </div>
                </div>

                {/* Notas Originales */}
                {selectedCheque.notas && (
                  <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Notas Originales</h4>
                    </div>
                    <p className="text-sm text-amber-900">{selectedCheque.notas}</p>
                  </div>
                )}

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex border-b border-gray-100 bg-gray-50/50">
                    <button
                      onClick={() => setChequeDetailTab('facturas')}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                        chequeDetailTab === 'facturas'
                          ? 'text-blue-600 bg-white border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <FileText size={16} />
                      Facturas ({selectedCheque.facturas?.length || 0})
                    </button>
                    <button
                      onClick={() => setChequeDetailTab('notas')}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                        chequeDetailTab === 'notas'
                          ? 'text-purple-600 bg-white border-b-2 border-purple-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Mail size={16} />
                      Notas Cliente ({selectedCheque.clientNotas?.length || 0})
                    </button>
                    <button
                      onClick={() => setChequeDetailTab('log')}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                        chequeDetailTab === 'log'
                          ? 'text-green-600 bg-white border-b-2 border-green-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <MessageSquare size={16} />
                      Log ({selectedCheque.logs?.length || 0})
                    </button>
                  </div>

                  <div className="p-5">
                    {/* Facturas Tab */}
                    {chequeDetailTab === 'facturas' && (
                      <>
                        {/* Add New Factura */}
                        <div className="mb-5 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Registrar Factura/Ajuste</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={newFacturaNumero}
                              onChange={(e) => setNewFacturaNumero(e.target.value)}
                              placeholder="# Factura"
                              className="px-3 py-2.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                            />
                            <input
                              type="text"
                              value={newFacturaNotas}
                              onChange={(e) => setNewFacturaNotas(e.target.value)}
                              placeholder="Notas (ej: ENVIADO AJUSTE)"
                              className="px-3 py-2.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                            />
                          </div>
                          <button
                            onClick={async () => {
                              if (!newFacturaNumero.trim() || !selectedCheque) return;
                              setAddingFactura(true);
                              try {
                                await chequeService.addFactura(selectedCheque.id, { 
                                  factura_numero: newFacturaNumero,
                                  notas: newFacturaNotas 
                                });
                                const res = await chequeService.getById(selectedCheque.id);
                                setSelectedCheque(res.data);
                                setNewFacturaNumero('');
                                setNewFacturaNotas('');
                              } catch (err) {
                                console.error('Error adding factura:', err);
                              } finally {
                                setAddingFactura(false);
                              }
                            }}
                            disabled={!newFacturaNumero.trim() || addingFactura}
                            className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 shadow-sm"
                          >
                            {addingFactura ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Plus size={16} />
                            )}
                            Agregar Factura
                          </button>
                        </div>

                        {/* Facturas List */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {selectedCheque.facturas && selectedCheque.facturas.length > 0 ? (
                            selectedCheque.facturas.map((factura) => (
                              <div key={factura.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-blue-600">#{factura.factura_numero}</span>
                                    {factura.notas && <span className="text-sm text-gray-600">{factura.notas}</span>}
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(factura.created_at).toLocaleString()}
                                    {factura.created_by_first_name && ` - ${factura.created_by_first_name}`}
                                  </p>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (!confirm('¿Eliminar esta factura?')) return;
                                    try {
                                      await chequeService.deleteFactura(factura.id);
                                      const res = await chequeService.getById(selectedCheque.id);
                                      setSelectedCheque(res.data);
                                    } catch (err) {
                                      console.error('Error deleting factura:', err);
                                    }
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <FileText size={24} className="text-gray-300" />
                              </div>
                              <p className="text-sm font-medium">Sin facturas registradas</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Notas Cliente Tab */}
                    {chequeDetailTab === 'notas' && (
                      <>
                        {/* Add New Client Note */}
                        <div className="mb-5 p-4 bg-purple-50/50 border border-purple-100 rounded-xl">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Escribir Nota para el Cliente</h4>
                          <textarea
                            value={newClientNota}
                            onChange={(e) => setNewClientNota(e.target.value)}
                            placeholder="Escribe una nota para el cliente..."
                            className="w-full px-3 py-2.5 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-sm resize-none"
                            rows={3}
                          />
                          <button
                            onClick={async () => {
                              if (!newClientNota.trim() || !selectedCheque) return;
                              setAddingClientNota(true);
                              try {
                                await chequeService.addNota(selectedCheque.id, { nota: newClientNota });
                                const res = await chequeService.getById(selectedCheque.id);
                                setSelectedCheque(res.data);
                                setNewClientNota('');
                              } catch (err) {
                                console.error('Error adding client nota:', err);
                              } finally {
                                setAddingClientNota(false);
                              }
                            }}
                            disabled={!newClientNota.trim() || addingClientNota}
                            className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 shadow-sm"
                          >
                            {addingClientNota ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Plus size={16} />
                            )}
                            Agregar Nota
                          </button>
                        </div>

                        {/* Client Notes List */}
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {selectedCheque.clientNotas && selectedCheque.clientNotas.length > 0 ? (
                            selectedCheque.clientNotas.map((nota: ChequeNota) => (
                              <div key={nota.id} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                  <span className="text-white font-bold text-xs">{nota.created_by_first_name?.charAt(0) || 'N'}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800">{nota.nota}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(nota.created_at).toLocaleString()}
                                    {nota.created_by_first_name && ` - ${nota.created_by_first_name}`}
                                  </p>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (!confirm('¿Eliminar esta nota?')) return;
                                    try {
                                      await chequeService.deleteNota(nota.id);
                                      const res = await chequeService.getById(selectedCheque.id);
                                      setSelectedCheque(res.data);
                                    } catch (err) {
                                      console.error('Error deleting nota:', err);
                                    }
                                  }}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Mail size={24} className="text-gray-300" />
                              </div>
                              <p className="text-sm font-medium">Sin notas para el cliente</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Log Tab */}
                    {chequeDetailTab === 'log' && (
                      <>
                        {/* Add New Log */}
                        <div className="mb-5 p-4 bg-green-50/50 border border-green-100 rounded-xl">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Agregar Nota al Log Interno</h4>
                          <textarea
                            value={newLogNote}
                            onChange={(e) => setNewLogNote(e.target.value)}
                            placeholder="Escribe una nota de seguimiento..."
                            className="w-full px-3 py-2.5 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-sm resize-none"
                            rows={3}
                          />
                          <button
                            onClick={async () => {
                              if (!newLogNote.trim() || !selectedCheque) return;
                              setAddingLog(true);
                              try {
                                await chequeService.addLog(selectedCheque.id, { nota: newLogNote });
                                const res = await chequeService.getById(selectedCheque.id);
                                setSelectedCheque(res.data);
                                setNewLogNote('');
                              } catch (err) {
                                console.error('Error adding log:', err);
                              } finally {
                                setAddingLog(false);
                              }
                            }}
                            disabled={!newLogNote.trim() || addingLog}
                            className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 shadow-sm"
                          >
                            {addingLog ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Plus size={16} />
                            )}
                            Agregar Nota
                          </button>
                        </div>

                        {/* Log History */}
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {selectedCheque.logs && selectedCheque.logs.length > 0 ? (
                            selectedCheque.logs.map((log) => (
                              <div key={log.id} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                  <span className="text-white font-bold text-xs">{log.first_name?.charAt(0) || 'L'}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800">{log.nota}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(log.created_at).toLocaleString()}
                                    {log.first_name && ` - ${log.first_name}`}
                                    {log.facturador && ` (${log.facturador})`}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <MessageSquare size={24} className="text-gray-300" />
                              </div>
                              <p className="text-sm font-medium">Sin notas en el log</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Actions */}
              {user?.role === 'admin' && (
                <div className="w-full lg:w-72 p-6 bg-white border-t lg:border-t-0 lg:border-l border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5">Panel de Control</h4>
                  
                  {/* Change Status */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Estado de Cheque</label>
                    <select
                      value={selectedCheque.status}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        setSelectedCheque({ ...selectedCheque, status: newStatus });
                        try {
                          await chequeService.update(selectedCheque.id, { status: newStatus });
                          if (selectedClient) {
                            const chequesRes = await chequeService.getByClient(selectedClient.id);
                            setClientCheques(chequesRes.data);
                          }
                        } catch (err) {
                          console.error('Error updating cheque status:', err);
                          setSelectedCheque({ ...selectedCheque });
                        }
                      }}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white transition-colors cursor-pointer font-medium"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="in_process">En Proceso</option>
                      <option value="completed">Completado</option>
                      <option value="denied">Denegado</option>
                      <option value="paid">Pagado</option>
                      <option value="ajuste">Ajuste</option>
                    </select>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 my-6"></div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={async () => {
                        setEditChequeData({
                          seguimiento_ck: selectedCheque.seguimiento_ck || '',
                          check_number: selectedCheque.check_number || '',
                          fecha_ck: selectedCheque.fecha_ck ? selectedCheque.fecha_ck.split('T')[0] : '',
                          cantidad: selectedCheque.cantidad || '',
                          plan: selectedCheque.plan || '',
                          notas: selectedCheque.notas || '',
                          facturador: selectedCheque.facturador || '',
                          status: selectedCheque.status || 'pending'
                        });
                        try {
                          const res = await planService.getAll();
                          setBillingPlans(res.data);
                        } catch (err) {
                          console.error('Error fetching plans:', err);
                        }
                        setIsEditingCheque(true);
                      }}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-green-600/20 hover:shadow-xl hover:shadow-green-600/30"
                    >
                      <Edit2 size={18} />
                      Editar Cheque
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('¿Estás seguro de que deseas eliminar este cheque?')) return;
                        try {
                          await chequeService.delete(selectedCheque.id);
                          setShowChequeLogModal(false);
                          setSelectedCheque(null);
                          if (selectedClient) {
                            const chequesRes = await chequeService.getByClient(selectedClient.id);
                            setClientCheques(chequesRes.data);
                          }
                        } catch (err) {
                          console.error('Error deleting cheque:', err);
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl font-semibold transition-all duration-200"
                    >
                      <Trash2 size={18} />
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Cheque Modal */}
      {isEditingCheque && selectedCheque && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Edit2 className="text-green-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Editar Cheque</h2>
                  <p className="text-xs text-gray-500">#{selectedCheque.check_number || selectedCheque.id}</p>
                </div>
              </div>
              <button onClick={() => setIsEditingCheque(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Row 1 */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Search size={12} className="text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"># Seguimiento</span>
                  </div>
                  <input
                    type="text"
                    value={editChequeData.seguimiento_ck}
                    onChange={(e) => setEditChequeData({...editChequeData, seguimiento_ck: e.target.value})}
                    placeholder="Número"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={12} className="text-green-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"># CK</span>
                  </div>
                  <input
                    type="text"
                    value={editChequeData.check_number}
                    onChange={(e) => setEditChequeData({...editChequeData, check_number: e.target.value})}
                    placeholder="Número"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Calendar size={12} className="text-amber-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Fecha CK</span>
                  </div>
                  <input
                    type="date"
                    value={editChequeData.fecha_ck}
                    onChange={(e) => setEditChequeData({...editChequeData, fecha_ck: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <DollarSign size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cantidad</span>
                  </div>
                  <input
                    type="text"
                    value={editChequeData.cantidad}
                    onChange={(e) => setEditChequeData({...editChequeData, cantidad: e.target.value})}
                    placeholder="0.00"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Plan + Estado + Facturador */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={12} className="text-green-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Plan Médico</span>
                  </div>
                  <select
                    value={editChequeData.plan}
                    onChange={(e) => setEditChequeData({...editChequeData, plan: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 cursor-pointer"
                  >
                    <option value="">Seleccionar</option>
                    {billingPlans.map(plan => (
                      <option key={plan.id} value={plan.plan_code}>{plan.plan_code} - {plan.plan_name}</option>
                    ))}
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FileText size={12} className="text-orange-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Estado</span>
                  </div>
                  <select
                    value={editChequeData.status}
                    onChange={(e) => setEditChequeData({...editChequeData, status: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 cursor-pointer"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_process">En Proceso</option>
                    <option value="completed">Completado</option>
                    <option value="denied">Denegado</option>
                    <option value="paid">Pagado</option>
                    <option value="ajuste">Ajuste</option>
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <User size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Facturador</span>
                  </div>
                  <input
                    type="text"
                    value={editChequeData.facturador}
                    onChange={(e) => setEditChequeData({...editChequeData, facturador: e.target.value})}
                    placeholder="Iniciales"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Notas */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <MessageSquare size={12} className="text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Notas</span>
                </div>
                <textarea
                  value={editChequeData.notas}
                  onChange={(e) => setEditChequeData({...editChequeData, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  rows={3}
                  className="w-full text-sm text-gray-800 bg-white border border-amber-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-400 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditingCheque(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    setSavingChequeEdit(true);
                    try {
                      await chequeService.update(selectedCheque.id, editChequeData);
                      const res = await chequeService.getById(selectedCheque.id);
                      setSelectedCheque(res.data);
                      if (selectedClient) {
                        const chequesRes = await chequeService.getByClient(selectedClient.id);
                        setClientCheques(chequesRes.data);
                      }
                      setIsEditingCheque(false);
                    } catch (err) {
                      console.error('Error updating cheque:', err);
                    } finally {
                      setSavingChequeEdit(false);
                    }
                  }}
                  disabled={savingChequeEdit}
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 text-sm font-medium shadow-sm"
                >
                  {savingChequeEdit ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Invoice Modal */}
      {showNewInvoiceModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="text-blue-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Nueva Factura</h2>
                  <p className="text-xs text-gray-500">{selectedClient.company_name || `${selectedClient.first_name} ${selectedClient.last_name}`}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowNewInvoiceModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedClient) return;
              setSavingNewInvoice(true);
              setNewInvoiceError('');
              try {
                await invoiceService.create({
                  ...newInvoiceData,
                  clientId: selectedClient.id,
                  planId: newInvoiceData.planId ? parseInt(newInvoiceData.planId) : null,
                  amount: newInvoiceData.amount ? parseFloat(newInvoiceData.amount) : 0,
                  initials: newInvoiceData.initials
                });
                const invoicesRes = await clientService.getInvoices(selectedClient.id);
                setClientInvoices(invoicesRes.data);
                setShowNewInvoiceModal(false);
              } catch (err: any) {
                setNewInvoiceError(err.response?.data?.error || 'Error al crear la factura');
              } finally {
                setSavingNewInvoice(false);
              }
            }} className="p-5 space-y-5">
              {newInvoiceError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {newInvoiceError}
                </div>
              )}

              {/* Info Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FileText size={12} className="text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"># Factura *</span>
                  </div>
                  <input
                    type="text"
                    value={newInvoiceData.invoiceNumber}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, invoiceNumber: e.target.value})}
                    placeholder="Número"
                    required
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={12} className="text-green-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Plan</span>
                  </div>
                  <select
                    value={newInvoiceData.planId}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, planId: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 cursor-pointer"
                  >
                    <option value="">Seleccionar</option>
                    {billingPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>{plan.plan_code} - {plan.plan_name}</option>
                    ))}
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <DollarSign size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Monto</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newInvoiceData.amount}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, amount: e.target.value})}
                    placeholder="0.00"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Row 2: Dates + Status + Facturador */}
              <div className="grid grid-cols-4 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Calendar size={12} className="text-amber-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Fecha Registro *</span>
                  </div>
                  <input
                    type="date"
                    value={newInvoiceData.invoiceDate}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, invoiceDate: e.target.value})}
                    required
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Calendar size={12} className="text-orange-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Fecha Servicio</span>
                  </div>
                  <input
                    type="date"
                    value={newInvoiceData.serviceDate}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, serviceDate: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <BarChart3 size={12} className="text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Estado</span>
                  </div>
                  <select
                    value={newInvoiceData.status}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, status: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 cursor-pointer"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_process">En Proceso</option>
                    <option value="completed">Completada</option>
                    <option value="denied">Denegada</option>
                    <option value="paid">Pagada</option>
                    <option value="ajuste">Ajuste</option>
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <User size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Facturador</span>
                  </div>
                  <input
                    type="text"
                    value={newInvoiceData.initials}
                    onChange={(e) => setNewInvoiceData({...newInvoiceData, initials: e.target.value})}
                    placeholder="Iniciales"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Pendiente */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <MessageSquare size={12} className="text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Pendiente</span>
                </div>
                <textarea
                  value={newInvoiceData.pendingNotes}
                  onChange={(e) => setNewInvoiceData({...newInvoiceData, pendingNotes: e.target.value})}
                  placeholder="Notas de pendiente..."
                  rows={2}
                  className="w-full text-sm text-gray-800 bg-white border border-amber-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-400 resize-none"
                />
              </div>

              {/* Gestión */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <FileText size={12} className="text-blue-500" />
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Gestión</span>
                </div>
                <textarea
                  value={newInvoiceData.managementNotes}
                  onChange={(e) => setNewInvoiceData({...newInvoiceData, managementNotes: e.target.value})}
                  placeholder="Notas de gestión..."
                  rows={2}
                  className="w-full text-sm text-gray-800 bg-white border border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowNewInvoiceModal(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingNewInvoice}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50 text-sm font-medium shadow-sm"
                >
                  {savingNewInvoice ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Guardar Factura
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Cheque Modal */}
      {showNewChequeModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Banknote className="text-green-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Nuevo Cheque</h2>
                  <p className="text-xs text-gray-500">{selectedClient.company_name || `${selectedClient.first_name} ${selectedClient.last_name}`}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowNewChequeModal(false);
                  setNewChequeData({ seguimiento_ck: '', check_number: '', fecha_ck: '', cantidad: '', plan: '', notas: '', facturador: '', status: 'pending' });
                  setNewChequeFacturas([]);
                  setNewChequeFacturaInput({ numero: '', notas: '' });
                }} 
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Info Cards Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Search size={12} className="text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"># Seguimiento</span>
                  </div>
                  <input
                    type="text"
                    value={newChequeData.seguimiento_ck}
                    onChange={(e) => setNewChequeData({...newChequeData, seguimiento_ck: e.target.value})}
                    placeholder="Número"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={12} className="text-green-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"># CK</span>
                  </div>
                  <input
                    type="text"
                    value={newChequeData.check_number}
                    onChange={(e) => setNewChequeData({...newChequeData, check_number: e.target.value})}
                    placeholder="Número"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Calendar size={12} className="text-amber-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Fecha CK</span>
                  </div>
                  <input
                    type="date"
                    value={newChequeData.fecha_ck}
                    onChange={(e) => setNewChequeData({...newChequeData, fecha_ck: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <DollarSign size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cantidad</span>
                  </div>
                  <input
                    type="text"
                    value={newChequeData.cantidad}
                    onChange={(e) => setNewChequeData({...newChequeData, cantidad: e.target.value})}
                    placeholder="0.00"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Plan + Estado + Facturador */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={12} className="text-green-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Plan Médico</span>
                  </div>
                  <select
                    value={newChequeData.plan}
                    onChange={(e) => setNewChequeData({...newChequeData, plan: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 focus:outline-none cursor-pointer appearance-none"
                  >
                    <option value="">Seleccionar</option>
                    {billingPlans.map(plan => (
                      <option key={plan.id} value={plan.plan_code}>{plan.plan_code} - {plan.plan_name}</option>
                    ))}
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FileText size={12} className="text-orange-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Estado</span>
                  </div>
                  <select
                    value={newChequeData.status}
                    onChange={(e) => setNewChequeData({...newChequeData, status: e.target.value})}
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 focus:outline-none cursor-pointer appearance-none"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_process">En Proceso</option>
                    <option value="completed">Completado</option>
                    <option value="denied">Denegado</option>
                    <option value="paid">Pagado</option>
                    <option value="ajuste">Ajuste</option>
                  </select>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <User size={12} className="text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Facturador</span>
                  </div>
                  <input
                    type="text"
                    value={newChequeData.facturador}
                    onChange={(e) => setNewChequeData({...newChequeData, facturador: e.target.value})}
                    placeholder="Iniciales"
                    className="w-full text-sm font-semibold text-gray-800 bg-transparent border-0 p-0 focus:ring-0 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Notas */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <MessageSquare size={12} className="text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Notas</span>
                </div>
                <textarea
                  value={newChequeData.notas}
                  onChange={(e) => setNewChequeData({...newChequeData, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  rows={3}
                  className="w-full text-sm text-gray-800 bg-white border border-amber-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-400 resize-none"
                />
              </div>

              {/* Registrar Factura/Ajuste */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <FileText size={12} className="text-blue-500" />
                  Registrar Factura/Ajuste
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newChequeFacturaInput.numero}
                    onChange={(e) => setNewChequeFacturaInput({...newChequeFacturaInput, numero: e.target.value})}
                    placeholder="# Factura"
                    className="px-3 py-2.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                  />
                  <input
                    type="text"
                    value={newChequeFacturaInput.notas}
                    onChange={(e) => setNewChequeFacturaInput({...newChequeFacturaInput, notas: e.target.value})}
                    placeholder="Notas (ej: ENVIADO AJUSTE)"
                    className="px-3 py-2.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                  />
                </div>
                <button
                  onClick={() => {
                    if (!newChequeFacturaInput.numero.trim()) return;
                    setNewChequeFacturas([...newChequeFacturas, { ...newChequeFacturaInput }]);
                    setNewChequeFacturaInput({ numero: '', notas: '' });
                  }}
                  disabled={!newChequeFacturaInput.numero.trim()}
                  className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 shadow-sm"
                >
                  <Plus size={16} />
                  Agregar Factura
                </button>

                {/* List of added facturas */}
                {newChequeFacturas.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                    {newChequeFacturas.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white border border-blue-100 rounded-lg px-3 py-2.5">
                        <div>
                          <span className="text-sm font-bold text-blue-600">#{f.numero}</span>
                          {f.notas && <span className="text-sm text-gray-600 ml-2">{f.notas}</span>}
                        </div>
                        <button
                          onClick={() => setNewChequeFacturas(newChequeFacturas.filter((_, i) => i !== idx))}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewChequeModal(false);
                    setNewChequeData({ seguimiento_ck: '', check_number: '', fecha_ck: '', cantidad: '', plan: '', notas: '', facturador: '', status: 'pending' });
                    setNewChequeFacturas([]);
                    setNewChequeFacturaInput({ numero: '', notas: '' });
                  }}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!selectedClient) return;
                    setSavingCheque(true);
                    try {
                      const createRes = await chequeService.create({
                        client_id: selectedClient.id,
                        ...newChequeData
                      });
                      const newChequeId = createRes.data.id;
                      for (const f of newChequeFacturas) {
                        await chequeService.addFactura(newChequeId, {
                          factura_numero: f.numero,
                          notas: f.notas,
                          facturador: newChequeData.facturador
                        });
                      }
                      const chequesRes = await chequeService.getByClient(selectedClient.id);
                      setClientCheques(chequesRes.data);
                      setShowNewChequeModal(false);
                      setNewChequeData({ seguimiento_ck: '', check_number: '', fecha_ck: '', cantidad: '', plan: '', notas: '', facturador: '', status: 'pending' });
                      setNewChequeFacturas([]);
                      setNewChequeFacturaInput({ numero: '', notas: '' });
                    } catch (err) {
                      console.error('Error creating cheque:', err);
                    } finally {
                      setSavingCheque(false);
                    }
                  }}
                  disabled={savingCheque}
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 text-sm font-medium shadow-sm"
                >
                  {savingCheque ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Guardar Cheque
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-200/50">
            {/* Header */}
            <div className="relative px-8 py-6 bg-white border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                    <FileText size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900">
                        #{selectedInvoice.invoice_number}
                      </h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getInvoiceStatusColor(selectedInvoice.status)}`}>
                        {invoiceStatusLabels[selectedInvoice.status]}
                      </span>
                    </div>
                    <p className="text-gray-500 mt-1">
                      {selectedClient?.company_name || `${selectedClient?.first_name} ${selectedClient?.last_name}`}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={closeInvoiceDetailModal} 
                  className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col lg:flex-row max-h-[calc(90vh-100px)]">
              {/* Left Panel - Main Content */}
              <div className="flex-1 p-8 overflow-y-auto bg-gray-50/50">
                {/* Key Info Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText size={16} className="text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{selectedInvoice.plan_code || '-'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign size={16} className="text-green-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">${Number(selectedInvoice.amount || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Calendar size={16} className="text-purple-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{new Date(selectedInvoice.invoice_date).toLocaleDateString('en-US')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Calendar size={16} className="text-orange-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Servicio</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{selectedInvoice.service_date ? new Date(selectedInvoice.service_date).toLocaleDateString('en-US') : '-'}</p>
                  </div>
                </div>

                {/* Pendiente Section */}
                {selectedInvoice.pending_notes && (
                  <div className="mb-8 p-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Pendiente</h4>
                    </div>
                    <p className="text-amber-900 leading-relaxed">{selectedInvoice.pending_notes}</p>
                  </div>
                )}

                {/* Notas de Gestión */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare size={18} className="text-blue-600" />
                      Notas de Gestión
                    </h3>
                    {!isAddingInvoiceGestion && (
                      <button
                        onClick={() => setIsAddingInvoiceGestion(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Plus size={16} />
                        Agregar Nota
                      </button>
                    )}
                  </div>
                  
                  <div className="p-5">
                    {/* Add Note Form */}
                    {isAddingInvoiceGestion && (
                      <div className="mb-5 p-5 bg-blue-50/50 border border-blue-100 rounded-xl">
                        <textarea
                          value={invoiceGestion}
                          onChange={(e) => setInvoiceGestion(e.target.value)}
                          placeholder="Escriba su nota de gestión aquí..."
                          className="w-full px-4 py-3 border border-blue-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none shadow-sm"
                          rows={3}
                          autoFocus
                        />
                        <div className="flex gap-3 justify-end mt-4">
                          <button
                            onClick={() => { setIsAddingInvoiceGestion(false); setInvoiceGestion(''); }}
                            className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handleSaveInvoiceGestion}
                            disabled={!invoiceGestion.trim() || isSavingInvoiceGestion}
                            className="px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                          >
                            {isSavingInvoiceGestion ? 'Guardando...' : 'Guardar Nota'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Notes List */}
                    <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                      {selectedInvoice.management_notes ? (
                        selectedInvoice.management_notes.split('\n\n').map((entry, idx) => {
                          const match = entry.match(/\[(.+?) - (.+?)\]: ([\s\S]*)/);
                          if (match) {
                            const [, dateTime, userName, content] = match;
                            return (
                              <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                  <span className="text-white font-bold text-sm">{userName.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="font-semibold text-gray-900 text-sm">{userName}</span>
                                    <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">{dateTime}</span>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed text-sm">{content}</p>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} className="p-4 bg-gray-50 rounded-xl text-gray-700 border border-gray-100">
                              {entry}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-12 text-gray-400">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageSquare size={28} className="text-gray-300" />
                          </div>
                          <p className="font-medium">Sin notas de gestión</p>
                          <p className="text-sm mt-1">Las notas aparecerán aquí</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Actions (Admin only) */}
              {user?.role === 'admin' && (
                <div className="w-full lg:w-72 p-6 bg-white border-t lg:border-t-0 lg:border-l border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5">Panel de Control</h4>
                  
                  {/* Change Status */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Estado de Factura</label>
                    <select
                      value={selectedInvoice.status}
                      onChange={(e) => handleInvoiceStatusChange(e.target.value as Invoice['status'])}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors cursor-pointer font-medium"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="in_process">En Proceso</option>
                      <option value="completed">Completada</option>
                      <option value="denied">Denegada</option>
                      <option value="refacturar">Re-Facturar</option>
                      <option value="paid">Pagada</option>
                      <option value="ajuste">Ajuste</option>
                    </select>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 my-6"></div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={startEditingInvoice}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30"
                    >
                      <Edit2 size={18} />
                      Editar Factura
                    </button>
                    <button
                      onClick={handleDeleteInvoice}
                      disabled={isDeletingInvoice}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50"
                    >
                      <Trash2 size={18} />
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Edit Modal */}
      {isEditingInvoice && selectedInvoice && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Editar Factura</h2>
                <p className="text-sm text-gray-500">#{selectedInvoice.invoice_number}</p>
              </div>
              <button onClick={() => setIsEditingInvoice(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Row 1: Invoice Number & Plan */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5"># Factura</label>
                  <input
                    type="text"
                    value={invoiceEditData.invoice_number || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, invoice_number: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Plan</label>
                  <select
                    value={invoiceEditData.plan_code || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, plan_code: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Seleccionar</option>
                    {billingPlans.map(plan => (
                      <option key={plan.id} value={plan.plan_code}>{plan.plan_code} - {plan.plan_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Amount & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Monto</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={invoiceEditData.amount || ''}
                      onChange={(e) => setInvoiceEditData({ ...invoiceEditData, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
                  <select
                    value={invoiceEditData.status || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, status: e.target.value as Invoice['status'] })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_process">En Proceso</option>
                    <option value="completed">Completada</option>
                    <option value="denied">Denegada</option>
                    <option value="refacturar">Re-Facturar</option>
                    <option value="paid">Pagada</option>
                    <option value="ajuste">Ajuste</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Dates + Facturador */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Factura</label>
                  <input
                    type="date"
                    value={invoiceEditData.invoice_date || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, invoice_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Servicio</label>
                  <input
                    type="date"
                    value={invoiceEditData.service_date || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, service_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Facturador</label>
                  <input
                    type="text"
                    value={invoiceEditData.initials || ''}
                    onChange={(e) => setInvoiceEditData({ ...invoiceEditData, initials: e.target.value })}
                    placeholder="Iniciales"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Pendiente Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notas Pendientes</label>
                <textarea
                  value={invoiceEditData.pending_notes || ''}
                  onChange={(e) => setInvoiceEditData({ ...invoiceEditData, pending_notes: e.target.value })}
                  placeholder="Notas sobre pendientes de esta factura..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end bg-gray-50">
              <button
                onClick={() => setIsEditingInvoice(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveInvoiceEdit}
                disabled={isSavingInvoice}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
              >
                {isSavingInvoice ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;
